import { booleanColor } from "@lib/helpers/ColorHelper";
import { Tag } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import TableIdButton from "@lib/components/MiniComponents/Buttons/TableIdButton";
import { shortenString } from "@lib/helpers/StringHelper";
import { ColumnConfig, generateColumns } from "@lib/helpers/TableHelper";
import { MdVerified } from "react-icons/md";
import CoinPriceTextFormat from "@lib/components/MiniComponents/Others/CoinPriceTextFormat";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { TbWorldSearch } from "react-icons/tb";
import { TableColumnsProps } from "@lib/types/General";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import WalletAddressText from "@lib/components/MiniComponents/Others/WalletAddress";
import {
  CustomerLeadName,
  CustomerName,
} from "@lib/components/MiniComponents/Others";
import { SendByteScannedTransaction } from "@lib/types";

type Props<T> = TableColumnsProps<T> & {
  isMobile: boolean;
};

const getColumns = <T extends SendByteScannedTransaction>(
  props: Props<T>,
): ColumnsType<T> => {
  const { messages, isMobile } = props;

  const columnsConfig: ColumnConfig<T>[] = [
    {
      title: "TX ID",
      dataIndex: "txId",
      width: "150px",
      render: (txId) => (
        <TableIdButton text={`${shortenString(txId)}`} textToBeCopied={txId} />
      ),
      filterType: "search",
    },

    {
      title: "common.customer",
      dataIndex: "customer.id",
      key: "customer.name",
      width: "150px",
      render: (_, r) => <CustomerName customer={r?.customer} link />,
    },

    {
      title: "common.customerLead",
      dataIndex: "customerUser.firstName",
      width: "150px",
      render: (_, r) => (
        <CustomerLeadName customerLead={r?.customerUser} link />
      ),
      filterType: "search",
    },

    {
      title: "common.walletAddress",
      dataIndex: "sendByteWalletAddress.address",
      width: "150px",
      render: (_, r) => (
        <WalletAddressText
          walletAddress={r?.sendByteWalletAddress}
          showNetwork={true}
          shortenAddress
        />
      ),
      filterType: "search",
    },

    {
      title: "common.fromWalletAddress",
      dataIndex: "fromWalletAddress",
      width: "230px",
      render: (_, r) => (
        <WalletAddressText
          walletAddress={{
            address: r?.fromWalletAddress || "",
            network: r?.sendByteWalletAddress?.network,
          }}
          showNetwork={true}
          shortenAddress
        />
      ),
      filterType: "search",
    },

    {
      title: "common.usdEquivalent",
      dataIndex: "convertedAmount",
      width: "145px",
      align: "right",
      render: (value, r) => (
        <CoinPriceTextFormat
          amount={value}
          currency={r?.convertedTo}
          showCurrency
          showCurrencyImage
          align={isMobile ? "left" : "right"}
        />
      ),
    },

    {
      title: "common.amount",
      dataIndex: "amount",
      width: "185px",
      align: "right",
      render: (value, r) => (
        <CoinPriceTextFormat
          amount={value}
          currency={r?.sendByteWalletAddress?.network}
          showCurrency={r?.sendByteWalletAddress?.network}
          showCurrencyImage
          align={isMobile ? "left" : "right"}
        />
      ),
      filterType: "search",
    },

    {
      title: "common.confirmStatus",
      dataIndex: "confirmed",
      width: "150px",
      render: (d) => (
        <Tag
          color={booleanColor(Boolean(d === "true"))}
          style={{
            display: "flex",
            alignItems: "center",
            width: "fit-content",
            gap: "3px",
          }}
        >
          {d === "true" && <MdVerified />}
          {
            (d === "true"
              ? messages["common.confirmed"]
              : messages["common.unconfirmed"]) as string
          }
        </Tag>
      ),
      filterType: "dropdown",
      filters: [
        { text: messages["common.confirmed"] as string, value: true },
        { text: messages["common.unconfirmed"] as string, value: false },
      ],
    },

    {
      title: "common.confirmedDate",
      dataIndex: "confirmedDate",
      width: "180px",
      render: (d) => dayjs(d).format("DD/MM/YYYY HH:mm"),
    },

    {
      title: "common.createdAt",
      dataIndex: "createdAt",
      width: "180px",
      render: (d) => dayjs(d).format("DD/MM/YYYY"),
    },
    {
      title: "#",
      dataIndex: "actions",
      className: "order-table-action",
      fixed: "right",
      width: "150px",
      render: (_, record) => (
        <StyledFlex className="actions">
          <ActionButton
            type="link"
            icon={<TbWorldSearch />}
            targetBlank
            href={record?.extraData?.transactionDetailUrl || null}
            disabled={!record?.extraData?.transactionDetailUrl || null}
            label={messages["common.blockChain"] as string}
            showBorder
          />
        </StyledFlex>
      ),
    },
  ];

  return generateColumns(props, columnsConfig);
};

export default getColumns;
